import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

const WARDS = ['General Ward A', 'General Ward B', 'Medical Ward', 'Surgical Ward', 'ICU', 'CCU', 'Paediatric Ward', 'Maternity Ward', 'Oncology Ward'];
const ICU_RESTRICTED = ['ICU', 'CCU'];
const ICU_SLOTS = ['08:00–09:00 (Morning)', '14:00–15:00 (Afternoon)', '18:00–19:00 (Evening)'];

function normalizeVisitor(v) {
  return { ...v, id: v.visitorId || v.id };
}

function VisitorEntryTab({ visitors, loading, error, reload, onCreate, onExit }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ visitorName: '', visitorPhone: '', relation: 'Spouse', patientName: '', patientUhid: '', ward: WARDS[0], noOfVisitors: 1, idProof: 'Aadhaar', slotBooked: '', remarks: '' });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentInside = visitors.filter(v => v.status === 'Inside' && v.visitDate === today).length;
  const todayTotal = visitors.filter(v => v.visitDate === today).length;
  const icuVisitors = visitors.filter(v => ICU_RESTRICTED.includes(v.ward) && v.visitDate === today);

  async function issuePass() {
    if (!form.visitorName.trim() || !form.visitorPhone.trim() || !form.patientName.trim()) {
      alert('Visitor name, phone, and patient name are required.');
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        ...form,
        noOfVisitors: Number(form.noOfVisitors || 1),
        entryTime: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
        visitDate: today,
      });
      setShowForm(false);
      setForm({ visitorName: '', visitorPhone: '', relation: 'Spouse', patientName: '', patientUhid: '', ward: WARDS[0], noOfVisitors: 1, idProof: 'Aadhaar', slotBooked: '', remarks: '' });
    } catch (err) {
      alert(err.message || 'Failed to issue pass');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {error && <p className="hint" style={{ color: '#dc2626', marginBottom: 10 }}>{error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button></p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Currently Inside', val: currentInside, color: '#f97316' }, { label: 'Total Today', val: todayTotal, color: '#6366f1' }, { label: 'ICU Visitors', val: icuVisitors.length, color: '#dc2626' }, { label: 'Exited', val: visitors.filter(v => v.status === 'Exited' && v.visitDate === today).length, color: '#10b981' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? 'Cancel' : '+ New Visitor Entry'}</button>
      </div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #6366f1' }}>
          <h3 className="full" style={{ marginTop: 0 }}>Visitor Pass Entry</h3>
          {ICU_RESTRICTED.includes(form.ward) && (
            <div className="full" style={{ padding: '10px 14px', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 8, fontSize: 12, color: '#dc2626', fontWeight: 700 }}>
              ICU/CCU Restricted Zone — Max 2 visitors per patient per slot. Pre-booking required.
            </div>
          )}
          <div className="field"><label>Visitor Name *</label><input className="input" required value={form.visitorName} onChange={e => setForm(f => ({ ...f, visitorName: e.target.value }))} /></div>
          <div className="field"><label>Mobile No. *</label><input className="input" required value={form.visitorPhone} onChange={e => setForm(f => ({ ...f, visitorPhone: e.target.value }))} /></div>
          <div className="field"><label>Relation to Patient</label>
            <select className="input" value={form.relation} onChange={e => setForm(f => ({ ...f, relation: e.target.value }))}>
              {['Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Sibling', 'Friend', 'Other'].map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="field"><label>ID Proof Type</label>
            <select className="input" value={form.idProof} onChange={e => setForm(f => ({ ...f, idProof: e.target.value }))}>
              {['Aadhaar', 'Voter ID', 'Driving License', 'Passport', 'PAN Card'].map(id => <option key={id}>{id}</option>)}
            </select>
          </div>
          <div className="field"><label>Patient Name *</label><input className="input" required value={form.patientName} onChange={e => setForm(f => ({ ...f, patientName: e.target.value }))} /></div>
          <div className="field"><label>Patient UHID</label><input className="input" value={form.patientUhid} onChange={e => setForm(f => ({ ...f, patientUhid: e.target.value }))} /></div>
          <div className="field"><label>Ward *</label>
            <select className="input" required value={form.ward} onChange={e => setForm(f => ({ ...f, ward: e.target.value }))}>
              {WARDS.map(w => <option key={w}>{w}</option>)}
            </select>
          </div>
          <div className="field"><label>No. of Visitors</label><input type="number" min="1" max="4" className="input" value={form.noOfVisitors} onChange={e => setForm(f => ({ ...f, noOfVisitors: e.target.value }))} /></div>
          {ICU_RESTRICTED.includes(form.ward) && (
            <div className="field"><label>Time Slot (ICU/CCU)</label>
              <select className="input" value={form.slotBooked} onChange={e => setForm(f => ({ ...f, slotBooked: e.target.value }))}>
                <option value="">Select Slot</option>
                {ICU_SLOTS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div className="field"><label>Remarks</label><input className="input" value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} /></div>
          <div className="full form-actions">
            <button type="button" className="btn btn-primary" disabled={saving} onClick={issuePass}>{saving ? 'Saving…' : 'Issue Pass & Grant Entry'}</button>
          </div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Today&apos;s Visitor Log — {today}</h3>
        {loading && visitors.length === 0 ? <p className="hint">Loading visitors…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead><tr><th>Pass No.</th><th>Visitor</th><th>Phone</th><th>Relation</th><th>Patient</th><th>Ward</th><th>No.</th><th>Entry</th><th>Exit</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {visitors.filter(v => v.visitDate === today).map(v => (
                <tr key={v.id}>
                  <td style={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 700 }}>{v.passNo}</td>
                  <td style={{ fontWeight: 700 }}>{v.visitorName}</td>
                  <td style={{ fontSize: 11 }}>{v.visitorPhone}</td>
                  <td style={{ fontSize: 11 }}>{v.relation}</td>
                  <td>{v.patientName}<div style={{ fontSize: 10, color: '#94a3b8' }}>{v.patientUhid}</div></td>
                  <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: ICU_RESTRICTED.includes(v.ward) ? '#fef2f2' : '#f8fafc', color: ICU_RESTRICTED.includes(v.ward) ? '#dc2626' : '#475569' }}>{v.ward}</span></td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{v.noOfVisitors}</td>
                  <td style={{ fontWeight: 700 }}>{v.entryTime}</td>
                  <td style={{ color: v.exitTime ? '#10b981' : '#94a3b8' }}>{v.exitTime || '—'}</td>
                  <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: v.status === 'Inside' ? '#fff7ed' : '#f0fdf4', color: v.status === 'Inside' ? '#f97316' : '#16a34a', border: `1px solid ${v.status === 'Inside' ? '#fed7aa' : '#86efac'}` }}>{v.status}</span></td>
                  <td>{v.status === 'Inside' && <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10 }} onClick={() => onExit(v.id)}>Exit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        {!loading && visitors.filter(v => v.visitDate === today).length === 0 && <p className="hint">No visitors registered today.</p>}
      </div>
    </div>
  );
}

export default function VisitorManagementPage() {
  const { data, loading, error, reload } = useLiveData(() => ipApi.getVisitors({ limit: 100 }), []);
  const visitors = (data?.visitors || []).map(normalizeVisitor);

  async function onCreate(body) {
    await ipApi.createVisitor(body);
    await reload();
  }

  async function onExit(visitorId) {
    try {
      await ipApi.patchVisitor(visitorId, {
        status: 'Exited',
        exitTime: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
      });
      await reload();
    } catch (err) {
      alert(err.message || 'Failed to mark exit');
    }
  }

  return (
    <PageShell title="Visitor Management" subtitle="Digital Visitor Register · Pass Generation · ICU Slot Booking · Entry/Exit Tracking — NABH FMS Compliant">
      <div style={{ padding: '10px 14px', background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10, marginBottom: 14, fontSize: 13, color: '#0c4a6e' }}>
        <strong>NABH FMS 3.1:</strong> All visitors must be registered with valid ID proof. ICU/CCU limited to 2 visitors/slot. Entry only during designated visiting hours.
      </div>
      <VisitorEntryTab visitors={visitors} loading={loading} error={error} reload={reload} onCreate={onCreate} onExit={onExit} />
    </PageShell>
  );
}
